import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import { api } from "../api/client";
import type { ArticleObservation } from "../api/types";
import { clockTime, safeHref } from "../components/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { TooltipIconButton } from "@/components/trading/TooltipIconButton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/useLanguage";
import { cn } from "@/lib/utils";

interface ArticleDrawerProps {
  articleId: string;
  siblingArticleIds: string[];
  expanded: boolean;
  onClose: () => void;
  onToggleExpanded: () => void;
  onSelectArticle: (articleId: string) => void;
}

export function ArticleDrawer({
  articleId,
  siblingArticleIds,
  expanded,
  onClose,
  onToggleExpanded,
  onSelectArticle,
}: ArticleDrawerProps) {
  const articleQuery = useQuery({
    queryKey: ["article", articleId],
    queryFn: () => api.getArticle(articleId),
  });
  const article = articleQuery.data;
  const { rtl } = useLanguage();
  // C5: prev/next chevrons flip in RTL so the arrow points to the
  // navigation direction the operator's eye expects.
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;
  const currentIndex = siblingArticleIds.indexOf(articleId);
  const previousId = currentIndex > 0 ? siblingArticleIds[currentIndex - 1] : null;
  const nextId =
    currentIndex >= 0 && currentIndex < siblingArticleIds.length - 1
      ? siblingArticleIds[currentIndex + 1]
      : null;
  const firstSeenObservation = article?.observations
    ?.filter((observation): observation is ArticleObservation => Boolean(observation.observed_at))
    .sort((a, b) => Date.parse(a.observed_at) - Date.parse(b.observed_at))[0];
  const firstSeenHref = safeHref(firstSeenObservation?.raw_url ?? article?.canonical_url ?? article?.url);

  return (
    <Sheet open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className={cn("w-full sm:max-w-2xl flex flex-col", expanded && "sm:max-w-4xl")}
      >
        <SheetHeader>
          <div className="flex items-center gap-1 mb-2">
            <TooltipIconButton
              label="Previous article"
              disabled={!previousId}
              onClick={() => previousId && onSelectArticle(previousId)}
            >
              <PrevIcon className="h-4 w-4" />
            </TooltipIconButton>
            <TooltipIconButton
              label="Next article"
              disabled={!nextId}
              onClick={() => nextId && onSelectArticle(nextId)}
            >
              <NextIcon className="h-4 w-4" />
            </TooltipIconButton>
            <TooltipIconButton
              label={expanded ? "Minimize article" : "Maximize article"}
              onClick={onToggleExpanded}
            >
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </TooltipIconButton>
          </div>
          {/* M12: publisher-supplied article titles wrap in <bdi> so
              latent bidi-override codepoints cannot reorder surrounding
              UI chrome. The fallback `articleId` is internal (UUID) so
              no isolation needed for that branch. */}
          <SheetTitle className="text-lg font-semibold leading-tight pe-8">
            {article?.title ? <bdi>{article.title}</bdi> : articleId}
          </SheetTitle>
          {article ? (
            <SheetDescription className="text-xs font-mono text-muted-foreground">
              {article.published_at ? `published ${clockTime(article.published_at)} · ` : ""}
              first seen {clockTime(article.first_seen_at)}
            </SheetDescription>
          ) : null}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
          {articleQuery.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading article…
            </div>
          ) : null}
          {articleQuery.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{(articleQuery.error as Error).message}</AlertDescription>
            </Alert>
          ) : null}
          {article ? (
            <>
              {firstSeenObservation ? (
                <div
                  className="rounded-md border-l-4 border-l-primary bg-primary/5 px-3 py-2 space-y-1.5"
                  aria-label="First seen source"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-primary">
                    <span className="inline-block h-3 w-0.5 bg-primary rounded-sm" aria-hidden="true" />
                    First seen source
                  </div>
                  {/* M12: raw_title is publisher-supplied — isolate. */}
                  <strong className="block text-sm leading-snug"><bdi>{firstSeenObservation.raw_title}</bdi></strong>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <code className="font-mono rounded bg-muted px-1.5 py-0.5">{firstSeenObservation.feed_id}</code>
                    <span>{clockTime(firstSeenObservation.observed_at)}</span>
                    {firstSeenHref ? (
                      <Button
                        asChild
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs font-normal"
                      >
                        <a href={firstSeenHref} target="_blank" rel="noreferrer noopener">source ↗</a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <article className="rounded-md bg-muted/30 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
                {article.body?.text ?? "No article body available."}
              </article>
              {article.observations && article.observations.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-primary">
                    <span className="inline-block h-3 w-0.5 bg-primary rounded-sm" aria-hidden="true" />
                    Observations
                  </div>
                  {article.observations.map((observation) => {
                    const href = safeHref(observation.raw_url);
                    return (
                      <div className="rounded border px-3 py-2 text-sm space-y-1" key={observation.id}>
                        <strong className="block leading-snug"><bdi>{observation.raw_title}</bdi></strong>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <code className="font-mono rounded bg-muted px-1.5 py-0.5">{observation.feed_id}</code>
                          <span>{clockTime(observation.observed_at)}</span>
                          {href ? (
                            <Button
                              asChild
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs font-normal"
                            >
                              <a href={href} target="_blank" rel="noreferrer noopener">link ↗</a>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
